(() => {
  'use strict';

  const SELECTORS = {
    timer: '[data-roma-cart-timer]',
    shipping: '[data-roma-ship]',
    socialProof: '[data-roma-live]',
    total: '[data-roma-total]',
    addMore: '[data-roma-addmore]',
    editor: '[data-roma-variant-editor]',
    option: '.roma-cart-option',
    status: '[data-roma-editor-status]'
  };

  const shopRoot = () => (window.Shopify?.routes?.root || '/');
  const route = (themeKey, fallback) => window.theme?.routes?.[themeKey] || `${shopRoot()}${fallback}`;

  const routes = {
    cart: () => `${shopRoot()}cart.js`,
    add: () => route('cart_add_url', 'cart/add.js'),
    change: () => route('cart_change_url', 'cart/change.js'),
    update: () => route('cart_update_url', 'cart/update.js')
  };

  function requestJSON(url, body) {
    const options = body
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }
      : { headers: { Accept: 'application/json' } };

    return fetch(url, options).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || Number(payload?.status) >= 400) {
        const error = new Error(payload?.description || payload?.message || 'Basket request failed');
        error.payload = payload;
        throw error;
      }
      return payload;
    });
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'en-GB';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${currency} ${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function tickTimers() {
    document.querySelectorAll(SELECTORS.timer).forEach((wrap) => {
      const minutes = Number.parseInt(wrap.dataset.minutes || '10', 10) || 10;
      const previous = Number.parseInt(wrap.dataset.left || String(minutes * 60 + 1), 10);
      const left = Math.max(0, previous - 1);
      wrap.dataset.left = String(left);

      const mins = String(Math.floor(left / 60)).padStart(2, '0');
      const secs = String(left % 60).padStart(2, '0');
      const template = wrap.dataset.template || 'Almost there – complete your order in [time] 🔥';
      wrap.innerHTML = template.replace('[time]', `<strong>${mins}:${secs}</strong>`);
    });
  }

  let timerId = null;
  function ensureTimer() {
    if (timerId || !document.querySelector(SELECTORS.timer)) return;
    tickTimers();
    timerId = window.setInterval(tickTimers, 1000);
  }

  function paintCart(cart) {
    const shipping = document.querySelector(SELECTORS.shipping);
    if (shipping && cart) {
      const goal = Number.parseInt(shipping.dataset.goal || '5999', 10) || 0;
      const total = Number(cart.total_price) || 0;
      const percentage = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 100;
      const fill = shipping.querySelector('[data-roma-ship-fill]');
      const label = shipping.querySelector('[data-roma-ship-label]');
      const confirmation = shipping.querySelector('.roma-cart-ship__ok');

      if (fill) fill.style.width = `${percentage}%`;
      if (total >= goal) {
        if (label) label.textContent = shipping.dataset.unlocked || 'Free delivery unlocked!';
        if (confirmation) confirmation.hidden = false;
      } else {
        const template = shipping.dataset.remaining || '[amount] to go until free delivery';
        if (label) label.textContent = template.replace('[amount]', money(goal - total));
        if (confirmation) confirmation.hidden = true;
      }
    }

    // Shopify emits cart:updated after quantity changes/removal. Native cart
    // progress is inside MiniCart, but the Livorno promo banner sits outside,
    // so section HTML replacement alone cannot keep that banner in sync.
    document.querySelectorAll('[data-livorno-free-shipping]').forEach((bar) => {
      const threshold = Math.max(0, Number(bar.dataset.goalCents) || 0);
      const excludedVariant = Number(bar.dataset.giftWrapVariant || 0);
      const giftTotal = excludedVariant && Array.isArray(cart?.items)
        ? cart.items.reduce((sum, item) => sum + (Number(item.variant_id) === excludedVariant ? Number(item.final_line_price || 0) : 0), 0)
        : 0;
      const subtotal = Math.max(0, (Number(cart?.total_price) || 0) - giftTotal);
      const unlocked = threshold <= 0 || subtotal >= threshold;
      const amount = Math.max(0, threshold - subtotal);
      const label = bar.querySelector('span.text-sm');
      if (label) {
        label.textContent = unlocked
          ? (bar.dataset.congratulations || 'Free delivery unlocked!')
          : (bar.dataset.remainingTemplate || '[amount] until free delivery').replace('[amount]', money(amount));
      }
      const progress = bar.querySelector('progress-bar');
      if (progress) progress.style.setProperty('--progress', `${threshold ? Math.min(100, Math.round(subtotal / threshold * 100)) : 100}%`);
    });

    const socialProof = document.querySelector(SELECTORS.socialProof);
    if (socialProof && !socialProof.dataset.set) {
      socialProof.dataset.set = 'true';
      const min = Number.parseInt(socialProof.dataset.min || '6', 10) || 6;
      const rawMax = Number.parseInt(socialProof.dataset.max || '13', 10) || 13;
      const max = Math.max(min, rawMax);
      const count = min + Math.floor(Math.random() * (max - min + 1));
      const target = socialProof.querySelector('[data-roma-live-text]');
      const template = socialProof.dataset.template || '[count] shoppers are checking out now';
      if (target) target.textContent = template.replace('[count]', String(count));
    }

    document.querySelectorAll('button[name="checkout"] .btn-text').forEach((label) => {
      const total = document.querySelector(SELECTORS.total);
      if (!total) return;
      const base = label.dataset.baseLabel || (label.textContent || 'Checkout').split('•')[0].trim();
      label.dataset.baseLabel = base;
      label.textContent = `${base} • ${total.textContent.trim()}`;
    });
  }

  let syncingAutomaticGifts = false;

  function syncAutomaticGifts(cart) {
    if (!cart || syncingAutomaticGifts || !Array.isArray(cart.items)) return Promise.resolve(false);
    const updates = {};
    cart.items.forEach((item) => {
      const properties = item.properties || {};
      if (String(properties['_Livorno auto gift'] || '') !== 'true') return;
      const parentProductId = Number.parseInt(properties['_Livorno parent product'] || '0', 10);
      const triggerQuantity = Number.parseInt(properties['_Livorno trigger quantity'] || '1', 10) || 1;
      if (!parentProductId) return;
      const parentQuantity = cart.items.reduce((total, line) => {
        const lineProperties = line.properties || {};
        const isGift = String(lineProperties['_Livorno auto gift'] || '') === 'true';
        return total + (!isGift && Number(line.product_id) === parentProductId ? Number(line.quantity || 0) : 0);
      }, 0);
      if (parentQuantity < triggerQuantity && item.key) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return Promise.resolve(false);
    syncingAutomaticGifts = true;
    return requestJSON(routes.update(), { updates })
      .then(() => true)
      .catch(() => false)
      .finally(() => { syncingAutomaticGifts = false; });
  }

  function refreshCartData() {
    return requestJSON(routes.cart())
      .then((cart) => syncAutomaticGifts(cart).then((changed) => ({ cart, changed })))
      .then(({ cart, changed }) => {
        if (changed) {
          refreshDrawer();
          return undefined;
        }
        paintCart(cart);
        return cart;
      })
      .catch(() => undefined);
  }

  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { open: true }
    }));
  }

  function applyDiscountCode(code) {
    if (!code) return Promise.resolve();
    return requestJSON(routes.update(), { discount: code }).catch(() => undefined);
  }

  function parseJSONScript(root, selector, fallback) {
    const script = root?.querySelector(selector);
    if (!script) return fallback;
    try {
      return JSON.parse(script.textContent || '') ?? fallback;
    } catch (_) {
      return fallback;
    }
  }

  function setEditorLoading(editor, loading) {
    editor.dataset.loading = loading ? 'true' : 'false';
    editor.querySelectorAll('select').forEach((select) => { select.disabled = loading; });
  }

  function setEditorStatus(editor, state) {
    const status = editor.querySelector(SELECTORS.status);
    if (!status) return;
    const key = {
      updating: 'updatingText',
      updated: 'updatedText',
      unavailable: 'unavailableText',
      error: 'errorText'
    }[state];
    status.textContent = key ? editor.dataset[key] || '' : '';
    status.dataset.state = state || '';
  }

  function lineContext(editor) {
    return parseJSONScript(editor, '.roma-cart-line-context', { properties: {}, sellingPlan: null });
  }

  function addItemPayload(variantId, context) {
    const item = { id: Number(variantId), quantity: 1 };
    if (context?.properties && Object.keys(context.properties).length) item.properties = context.properties;
    if (context?.sellingPlan) item.selling_plan = context.sellingPlan;
    return { items: [item] };
  }

  function updateOneUnitVariant(select) {
    const unit = select.closest('.roma-cart-unit');
    const editor = select.closest(SELECTORS.editor);
    if (!unit || !editor || editor.dataset.loading === 'true') return;

    const variants = parseJSONScript(editor, '.roma-cart-variants', []);
    const selectedOptions = [...unit.querySelectorAll(SELECTORS.option)].map((element) => element.value);
    const matched = variants.find((variant) =>
      Array.isArray(variant?.options) &&
      variant.options.length === selectedOptions.length &&
      variant.options.every((value, index) => value === selectedOptions[index])
    );

    if (!matched?.available) {
      setEditorStatus(editor, 'unavailable');
      window.setTimeout(refreshDrawer, 900);
      return;
    }

    const currentVariantId = Number.parseInt(editor.dataset.variantId || '0', 10);
    const currentQuantity = Number.parseInt(editor.dataset.quantity || '1', 10);
    const key = editor.dataset.key;
    if (!key || currentQuantity < 1 || Number(matched.id) === currentVariantId) return;

    const context = lineContext(editor);
    setEditorLoading(editor, true);
    setEditorStatus(editor, 'updating');

    // Decrement first. Shopify can regenerate line-item keys after any basket mutation,
    // so the original key is safest before a second request is made.
    requestJSON(routes.change(), { id: key, quantity: Math.max(0, currentQuantity - 1) })
      .then(() => requestJSON(routes.add(), addItemPayload(matched.id, context)))
      .then(() => {
        setEditorStatus(editor, 'updated');
        refreshDrawer();
      })
      .catch(() => {
        setEditorStatus(editor, 'error');
        // Best-effort rollback if the original line was already decremented.
        requestJSON(routes.add(), addItemPayload(currentVariantId, context))
          .catch(() => undefined)
          .finally(refreshDrawer);
      })
      .finally(() => setEditorLoading(editor, false));
  }

  function addOneMore(button) {
    const key = button.dataset.key;
    const quantity = (Number.parseInt(button.dataset.qty || '1', 10) || 1) + 1;
    if (!key) return;

    button.disabled = true;
    requestJSON(routes.change(), { id: key, quantity })
      .then(() => applyDiscountCode(button.dataset.bundleCode || ''))
      .then(refreshDrawer)
      .finally(() => { button.disabled = false; });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(SELECTORS.addMore);
    if (!button) return;
    event.preventDefault();
    addOneMore(button);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest(SELECTORS.option);
    if (select) updateOneUnitVariant(select);
  });

  // Quantity and remove actions use the native theme cart:updated event.
  // Update promotional shipping immediately from the returned cart object;
  // don't wait for the drawer to be opened or refreshed again.
  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail?.cart;
    if (cart && typeof cart.total_price === 'number') paintCart(cart);
    else refreshCartData();
  });

  document.addEventListener('cart:refresh', () => {
    window.setTimeout(() => {
      ensureTimer();
      refreshCartData();
    }, 450);
  });

  document.addEventListener('shopify:section:load', () => {
    ensureTimer();
    refreshCartData();
  });

  const initialise = () => {
    ensureTimer();
    refreshCartData();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
